import { useState } from "react";
import { INCOME_STATS, RECENT_JOBS, UPSKILL_SUGGESTIONS } from "../../../mocks/jobs";

const NEARBY_JOBS = [
  { id: 1, title: "Crew Member", type: "Landscaping", pay: "$22/hr", distance: "1.2 mi", color: "bg-emerald-500" },
  { id: 2, title: "Moving Help", type: "Labor", pay: "$28/hr", distance: "0.8 mi", color: "bg-rose-500" },
  { id: 3, title: "Office Cleaning", type: "Cleaning", pay: "$18/hr", distance: "2.4 mi", color: "bg-orange-400" },
  { id: 4, title: "Event Setup", type: "Events", pay: "$20/hr", distance: "3.1 mi", color: "bg-amber-400" },
  { id: 5, title: "Site Laborer", type: "Construction", pay: "$32/hr", distance: "4.5 mi", color: "bg-yellow-500" },
];

const MOBILE_STATS = [
  { label: "Total Earned", value: INCOME_STATS.totalEarned, icon: "ri-money-dollar-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
  { label: "This Week", value: INCOME_STATS.thisWeek, icon: "ri-calendar-line", color: "text-orange-500", bg: "bg-orange-50" },
  { label: "Jobs Done", value: String(INCOME_STATS.completedJobs), icon: "ri-check-double-line", color: "text-emerald-700", bg: "bg-emerald-50" },
  { label: "Your Rating", value: `${INCOME_STATS.rating} ⭐`, icon: "ri-star-line", color: "text-amber-500", bg: "bg-amber-50" },
];

export default function DashboardSection() {
  const [availableNow, setAvailableNow] = useState(false);

  return (
    <section id="dashboard" className="py-16 md:py-24 bg-[#f7f9f8]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-4 py-1.5 rounded-full mb-4">
            Income Dashboard
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Track Every Dollar.
            <br />
            <span className="text-emerald-500">Grow Every Day.</span>
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
            Your personal income command center — live earnings, job history, and nearby gigs in one view.
          </p>
        </div>

        {/* ── MOBILE VIEW ── */}
        <div className="md:hidden flex flex-col gap-4">

          {/* Available Now toggle */}
          <div className="bg-white rounded-2xl px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-extrabold text-gray-900">Available for Work</div>
              <div className="text-xs text-gray-500">Let employers find you right now</div>
            </div>
            <div className="flex items-center gap-2">
              {availableNow && (
                <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  Live
                </span>
              )}
              <button
                onClick={() => setAvailableNow(!availableNow)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 cursor-pointer ${availableNow ? "bg-emerald-500" : "bg-gray-300"}`}
                aria-label="Toggle availability"
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${availableNow ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>

          {/* 4 key stats */}
          <div className="grid grid-cols-2 gap-3">
            {MOBILE_STATS.map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl p-4">
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${stat.bg} mb-2`}>
                  <i className={`${stat.icon} ${stat.color} text-base`} />
                </div>
                <div className="text-xl font-extrabold text-gray-900">{stat.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Earnings bar */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-bold text-sm">Monthly Goal</span>
              <span className="text-emerald-100 text-xs font-semibold">78% reached</span>
            </div>
            <div className="w-full h-2.5 rounded-full bg-white/20 overflow-hidden mb-3">
              <div className="h-2.5 rounded-full bg-white transition-all duration-700" style={{ width: "78%" }} />
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-emerald-100">$3,120 earned</span>
              <span className="text-white font-extrabold">Goal: $4,000</span>
            </div>
          </div>

          {/* Nearby jobs */}
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-extrabold text-gray-900 text-sm">Jobs Near You</h3>
              <span className="text-xs text-emerald-600 font-semibold">{NEARBY_JOBS.length} available</span>
            </div>
            <div className="flex flex-col gap-2.5">
              {NEARBY_JOBS.map((job) => (
                <div key={job.id} className="flex items-center gap-3">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${job.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-gray-900">{job.title}</div>
                    <div className="text-[10px] text-gray-400">{job.type} · {job.distance}</div>
                  </div>
                  <span className="text-xs font-extrabold text-gray-900 whitespace-nowrap">{job.pay}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <a
            href="/dashboard"
            className="w-full py-4 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-2xl text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-dashboard-line" />
            Open My Dashboard
          </a>
        </div>

        {/* ── DESKTOP VIEW (full mock) ── */}
        <div className="hidden md:block bg-white rounded-3xl border border-gray-200 overflow-hidden">
          {/* Dashboard Header Bar */}
          <div className="bg-gray-900 px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/8b9b321cd7d670b6a60b0d1b6baf2111/e3a635c31b7543fa0ee8e8d84db9d640.png"
                alt="Gig n' Cash"
                className="h-6 w-auto object-contain"
              />
              <span className="text-white/50 text-sm">|</span>
              <span className="text-white text-sm font-semibold">My Dashboard</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">Available Now</span>
              <button
                onClick={() => setAvailableNow(!availableNow)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 cursor-pointer ${availableNow ? "bg-emerald-500" : "bg-gray-600"}`}
                aria-label="Toggle availability"
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${availableNow ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              {availableNow && (
                <span className="text-xs text-emerald-400 font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  Live
                </span>
              )}
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Stats + Recent */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: "Total Earned", value: INCOME_STATS.totalEarned, icon: "ri-money-dollar-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
                  { label: "This Week", value: INCOME_STATS.thisWeek, icon: "ri-calendar-line", color: "text-orange-500", bg: "bg-orange-50" },
                  { label: "Active Jobs", value: String(INCOME_STATS.activeJobs), icon: "ri-briefcase-4-line", color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Pending Offers", value: String(INCOME_STATS.pendingOffers), icon: "ri-inbox-line", color: "text-rose-500", bg: "bg-rose-50" },
                  { label: "Your Rating", value: `⭐ ${INCOME_STATS.rating}`, icon: "ri-star-line", color: "text-yellow-600", bg: "bg-yellow-50" },
                  { label: "Jobs Completed", value: String(INCOME_STATS.completedJobs), icon: "ri-check-double-line", color: "text-emerald-700", bg: "bg-emerald-50" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-gray-50 rounded-xl border border-gray-100 p-4">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${stat.bg} mb-2`}>
                      <i className={`${stat.icon} ${stat.color} text-base`} />
                    </div>
                    <div className="text-xl font-extrabold text-gray-900">{stat.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-sm mb-3">Recent Jobs</h3>
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left text-xs font-bold text-gray-500 px-4 py-2.5">Job</th>
                        <th className="text-left text-xs font-bold text-gray-500 px-4 py-2.5 hidden sm:table-cell">Date</th>
                        <th className="text-left text-xs font-bold text-gray-500 px-4 py-2.5 hidden md:table-cell">Hours</th>
                        <th className="text-right text-xs font-bold text-gray-500 px-4 py-2.5">Earned</th>
                        <th className="text-right text-xs font-bold text-gray-500 px-4 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {RECENT_JOBS.map((job, i) => (
                        <tr key={job.id} className={`${i < RECENT_JOBS.length - 1 ? "border-b border-gray-50" : ""}`}>
                          <td className="px-4 py-3 text-xs font-semibold text-gray-800">{job.title}</td>
                          <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">{job.date}</td>
                          <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{job.hours}h</td>
                          <td className="px-4 py-3 text-xs font-bold text-emerald-700 text-right">{job.earned}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-xs bg-emerald-100 text-emerald-700 font-bold px-2 py-1 rounded-full">{job.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Right: Upskill + Nearby */}
            <div className="flex flex-col gap-5">
              <div className="bg-gradient-to-br from-emerald-50 to-amber-50 rounded-2xl border border-emerald-100 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-sparkling-2-line text-emerald-600 text-lg" />
                  <h3 className="font-bold text-gray-900 text-sm">Upskill Engine</h3>
                </div>
                <p className="text-xs text-gray-500 mb-4">Certifications that unlock higher pay:</p>
                <div className="flex flex-col gap-3">
                  {UPSKILL_SUGGESTIONS.map((s) => (
                    <div key={s.title} className="bg-white rounded-xl border border-gray-200 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <i className={`${s.icon} text-emerald-600 text-sm`} />
                        <span className="text-xs font-bold text-gray-800">{s.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 line-through">{s.currentPay}</span>
                        <i className="ri-arrow-right-line text-gray-400 text-xs" />
                        <span className="text-xs font-extrabold text-emerald-700">{s.newPay}</span>
                        <span className="ml-auto text-xs text-gray-400">{s.timeToComplete}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900 text-sm">Near You</h3>
                  <span className="text-xs text-emerald-600 font-semibold cursor-pointer hover:underline">View all</span>
                </div>
                <div className="flex flex-col gap-2">
                  {NEARBY_JOBS.map((job) => (
                    <div key={job.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${job.color}`} />
                        <div>
                          <div className="text-xs font-bold text-gray-800">{job.title}</div>
                          <div className="text-xs text-gray-400">{job.type} · {job.distance}</div>
                        </div>
                      </div>
                      <span className="text-xs font-extrabold text-gray-900">{job.pay}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
